import { Activity, Check, Download, FileText, Hammer, MessageSquareQuote, Terminal } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { videoUrl } from "../api";
import { pipelineSourceKind, pipelineStageIndex, pipelineStages } from "../lib/pipelineStages";
import { useIsMobile } from "../lib/useIsMobile";
import { CaptionTab, LogsTab, TranscriptTab } from "./editClip/ClipTabs";
import { InputsPanel } from "./editClip/InputsPanel";
import { isTerminalStatus, type JobView } from "./editClip/jobView";
import { StudioModal } from "./editClip/StudioModal";
import { AvSyncCard, SyncCard } from "./editClip/SyncCards";

/**
 * Details — everything the video page used to spread across cards, rails
 * and tabs, behind one button (spec 2026-09-16 §2.2): the sync verdicts,
 * how the video was made (plain-language pipeline + what was asked for),
 * the captions, the log, and the files. Esc / outside click close it.
 */
type DetailsTab = "sync" | "made" | "transcript" | "logs" | "files";

const TABS: ReadonlyArray<{ id: DetailsTab; label: string; Icon: typeof Check }> = [
  { id: "sync", label: "Sync", Icon: Activity },
  { id: "made", label: "How it was made", Icon: Hammer },
  { id: "transcript", label: "Transcript", Icon: FileText },
  { id: "logs", label: "Log", Icon: Terminal },
  { id: "files", label: "Files", Icon: Download },
];

export function ClipDetailsDialog({
  view,
  captionLanguage,
  onClose,
  onJobStarted,
}: {
  view: JobView;
  captionLanguage: string;
  onClose: () => void;
  /** The Sync tab's fixes start a render — the page follows it. */
  onJobStarted?: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<DetailsTab>("sync");
  const ids = useId();
  const tabId = (t: DetailsTab): string => `${ids}-tab-${t}`;
  const panelId = (t: DetailsTab): string => `${ids}-panel-${t}`;
  // Arrow keys move between tabs (wrapping), Home/End jump; the moved-to tab
  // takes focus and opens — the WAI-ARIA automatic-activation pattern.
  const onTablistKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const idx = TABS.findIndex((t) => t.id === tab);
    let next = idx;
    if (e.key === "ArrowRight") next = (idx + 1) % TABS.length;
    else if (e.key === "ArrowLeft") next = (idx - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = TABS.length - 1;
    else return;
    e.preventDefault();
    const target = TABS[next]!.id;
    setTab(target);
    document.getElementById(tabId(target))?.focus();
  };
  return (
    <StudioModal title="Details" onClose={onClose} width={820}>
      <div
        role="tablist"
        aria-label="Details"
        onKeyDown={onTablistKey}
        style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,.06)", marginBottom: 16, overflowX: "auto" }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            id={tabId(t.id)}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            aria-controls={panelId(t.id)}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            style={{
              flex: isMobile ? "0 0 auto" : 1,
              padding: isMobile ? "10px 10px" : "10px 14px",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              background: tab === t.id ? "rgba(255,213,0,.04)" : "transparent",
              color: tab === t.id ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontSize: isMobile ? 11.5 : 12.5,
              fontWeight: 600,
              borderBottom: tab === t.id ? "2px solid var(--ql-yellow)" : "2px solid transparent",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              whiteSpace: "nowrap",
            }}
          >
            <t.Icon size={13} strokeWidth={1.85} />
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === "sync" && (
          <div>
            <AvSyncCard jobId={view.id} blocked={view.status !== "ready" || !view.hasVideo} />
            <SyncCard jobId={view.id} onJobStarted={onJobStarted} />
          </div>
        )}
        {tab === "made" && <HowItWasMade view={view} captionLanguage={captionLanguage} />}
        {tab === "transcript" && <TranscriptTab jobId={view.id} hasVideo={view.hasVideo} />}
        {tab === "logs" && <LogsTab jobId={view.id} />}
        {tab === "files" && <FilesTab view={view} captionLanguage={captionLanguage} />}
      </div>
    </StudioModal>
  );
}

/** The plain-language pipeline with a check on every step that ran, then
 *  the submission that produced it. */
function HowItWasMade({ view, captionLanguage }: { view: JobView; captionLanguage: string }): React.JSX.Element {
  const stages = useMemo(
    () => pipelineStages(captionLanguage, pipelineSourceKind(view.input, view.sourceUrl)),
    [captionLanguage, view.input, view.sourceUrl],
  );
  const idx = pipelineStageIndex(stages, view.status, view.currentStage);
  const stopped = view.status === "failed" || view.status === "incomplete";
  const running = !isTerminalStatus(view.status);
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 14 }}>
        {stages.map((s, i) => {
          const done = i < idx;
          const here = i === idx;
          const color = done ? "#4ADE80" : here && stopped ? "var(--ql-danger)" : here && running ? "var(--ql-yellow)" : "var(--ql-fg-subtle)";
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 4px", fontSize: 13 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? "rgba(74,222,128,.14)" : here && stopped ? "rgba(248,113,113,.14)" : here && running ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
                  color,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {done ? <Check size={12} strokeWidth={3} /> : i + 1}
              </span>
              <span style={{ color: done ? "var(--ql-gray-200)" : color, fontWeight: done || here ? 600 : 500 }}>
                {s.label}
              </span>
              {here && stopped && (
                <span style={{ fontSize: 11, color: "var(--ql-danger)" }}>· stopped here</span>
              )}
              {here && running && (
                <span style={{ fontSize: 11, color: "var(--ql-yellow)" }}>· running</span>
              )}
            </div>
          );
        })}
      </div>
      {view.input ? (
        <InputsPanel input={view.input} fallbackUrl={view.sourceUrl} jobId={view.id} />
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)" }}>
          The original request wasn't saved for this video.
        </div>
      )}
    </div>
  );
}

function FilesTab({ view, captionLanguage }: { view: JobView; captionLanguage: string }): React.JSX.Element {
  const captionLang = captionLanguage.trim() || "Arabic";
  return (
    <div>
      <ArtefactRow ok={view.hasTranscript} label="Transcript" />
      <ArtefactRow ok={view.hasCaption || view.status === "ready"} label={`${captionLang} captions (SRT)`} />
      <ArtefactRow
        ok={view.hasVideo}
        label="Vertical video (MP4)"
        action={
          view.hasVideo ? (
            <a
              href={videoUrl(view.id)}
              download={`${view.id}-ar.mp4`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ql-yellow)", textDecoration: "none" }}
            >
              <Download size={12} strokeWidth={2} /> Download
            </a>
          ) : undefined
        }
      />
      <ArtefactRow ok={view.hasCaption} label="Post caption" />
      {view.hasCaption && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>
            <MessageSquareQuote size={12} strokeWidth={2} /> Post caption
          </div>
          <CaptionTab jobId={view.id} hasVideo={view.hasVideo} />
        </div>
      )}
    </div>
  );
}

function ArtefactRow({ ok, label, action }: { ok: boolean; label: string; action?: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 0",
        fontSize: 13,
        color: ok ? "var(--ql-gray-200)" : "var(--ql-fg-subtle)",
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 999,
          background: ok ? "rgba(74,222,128,.14)" : "rgba(255,255,255,.04)",
          color: ok ? "#4ADE80" : "var(--ql-fg-subtle)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {ok ? <Check size={11} strokeWidth={3} /> : null}
      </div>
      <span style={{ flex: 1 }}>{label}</span>
      {action}
    </div>
  );
}
